import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Paper, Skeleton } from '@mui/material';

/* Loading skeleton sized to match the *real* layout it's standing in for —
   not a generic "loading rectangle". The hero shape matches MenuHero
   (logo circle + name + tagline + table chip + actions cluster), and the
   grid uses the same explicit breakpoint chain as .menu-grid--cards so
   the transition into real cards is fluid, not a re-layout jolt. */
export default function MenuLoadingSkeleton() {
  const { t } = useTranslation();
  return (
    <div
      className="menu-skeleton"
      role="status"
      aria-busy="true"
      aria-label={t('menu.loadingLabel', 'Loading menu')}
    >
      {/* Hero skeleton — mirrors MenuHero's two-column brand+actions grid */}
      <Paper
        elevation={0}
        className="menu-skeleton-hero"
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr auto',
          alignItems: 'center',
          gap: 2,
          p: 3,
          mb: 2,
          borderRadius: '24px',
          background: 'linear-gradient(135deg, var(--gray-100), var(--gray-200))',
        }}
      >
        <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 2, alignItems: 'center', minWidth: 0 }}>
          <Skeleton variant="rounded" width={56} height={56} sx={{ borderRadius: '14px' }} />
          <Box>
            <Skeleton variant="text" width="60%" height={28} />
            <Skeleton variant="text" width="80%" height={18} sx={{ mt: 0.5 }} />
            <Skeleton variant="rounded" width={120} height={24} sx={{ mt: 1, borderRadius: '9999px' }} />
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Skeleton variant="circular" width={40} height={40} />
          <Skeleton variant="circular" width={40} height={40} />
          <Skeleton variant="circular" width={40} height={40} />
        </Box>
      </Paper>

      {/* Sticky-nav skeleton — search + chip row */}
      <Box className="menu-skeleton-nav" sx={{ mb: 2 }}>
        <Skeleton variant="rounded" height={44} sx={{ mb: 1.5, borderRadius: '8px' }} />
        <Box sx={{ display: 'flex', gap: 1, overflow: 'hidden' }}>
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} variant="rounded" width={110} height={40} sx={{ borderRadius: '9999px', flexShrink: 0 }} />
          ))}
        </Box>
      </Box>

      {/* Card grid skeleton — matches Phase 1 explicit breakpoint chain */}
      <Box
        className="menu-skeleton-grid"
        sx={{
          display: 'grid',
          gap: 'var(--menu-stack-lg, 16px)',
          gridTemplateColumns: '1fr',
          '@media (min-width: 640px)': { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' },
          '@media (min-width: 1024px)': { gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 'var(--menu-stack-xl, 24px)' },
          '@media (min-width: 1440px)': { gridTemplateColumns: 'repeat(4, minmax(0, 1fr))' },
        }}
      >
        {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
          <Paper
            key={i}
            elevation={0}
            sx={{
              borderRadius: 'var(--menu-radius-card, 20px)',
              overflow: 'hidden',
              border: '1px solid var(--menu-border-subtle, #eef2f0)',
            }}
          >
            {/* 4:3 image area to match card aspect-ratio */}
            <Skeleton variant="rectangular" sx={{ width: '100%', aspectRatio: '4 / 3' }} />
            <Box sx={{ p: 2 }}>
              <Skeleton variant="text" width="70%" height={22} />
              <Skeleton variant="text" width="90%" height={16} sx={{ mt: 0.5 }} />
              <Skeleton variant="text" width="35%" height={20} sx={{ mt: 1 }} />
            </Box>
            <Box sx={{ px: 2, pb: 2 }}>
              <Skeleton variant="rounded" height={44} sx={{ borderRadius: '12px' }} />
            </Box>
          </Paper>
        ))}
      </Box>

      <span
        style={{
          position: 'absolute',
          width: 1,
          height: 1,
          padding: 0,
          margin: -1,
          overflow: 'hidden',
          clip: 'rect(0,0,0,0)',
          whiteSpace: 'nowrap',
          border: 0,
        }}
      >
        {t('menu.loadingLabel', 'Loading menu')}
      </span>
    </div>
  );
}
